import { useState, useEffect, useRef, useCallback } from 'react';
import { ajax } from 'jquery';
import './index.css';
import CommitEditor from '../editor/editor';

function replaceSpacesWithUnderscoresUsingReplaceAll(str: string): string {
    return str.replaceAll(' ', '_');
}

function formatDate(dateString: string): string {
    if (!dateString) return '';

    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');

    return `${year}-${month}-${day}`;
}

export default function AdminNotes() {
    const [featuredNotes, setFeaturedNotes] = useState<any[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [currentComicData, setCurrentComicData] = useState<any>(null);
    const [comicModalVisible, setComicModalVisible] = useState<boolean>(false);
    const [shareLinkCopied, setShareLinkCopied] = useState<boolean>(false);
    const [likedNoteIds, setLikedNoteIds] = useState<number[]>([]);

    // 分页相关状态
    const [page, setPage] = useState<number>(1);
    const [hasMore, setHasMore] = useState<boolean>(true);
    const [loadingMore, setLoadingMore] = useState<boolean>(false);
    const observer = useRef<IntersectionObserver | null>(null);
    const lastNoteElementRef = useRef<HTMLDivElement | null>(null);

    // 加载数据的函数
    const loadFeaturedNotes = async (pageNum: number, append: boolean = false) => {
        try {
            if (pageNum === 1) {
                setLoading(true);
            } else {
                setLoadingMore(true);
            }

            const result = await ajax({
                method: "GET",
                url: `/api/v1/countryball_card/admin/note/list?page=${pageNum}&limit=10`,
            });

            if (result.length === 0) {
                setHasMore(false);
            } else {
                if (append) {
                    setFeaturedNotes(prev => [...prev, ...result]);
                } else {
                    setFeaturedNotes(result);
                }
                setPage(pageNum);
            }
        } catch (err) {
            console.error('Failed to load featured notes:', err);
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    };

    // 初始加载
    useEffect(() => {
        loadFeaturedNotes(1);
    }, []);

    // 设置观察者，用于监测列表底部元素是否可见
    const lastNoteRef = useCallback((node: HTMLDivElement) => {
        if (loadingMore) return;

        if (observer.current) {
            observer.current.disconnect();
        }

        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && hasMore && !loadingMore) {
                loadFeaturedNotes(page + 1, true);
            }
        });

        if (node) {
            observer.current.observe(node);
            lastNoteElementRef.current = node;
        }
    }, [hasMore, loadingMore, page]);

    const showComic = (comic: string) => {
        if (!comic) return;

        try {
            const comicData = JSON.parse(comic);
            setCurrentComicData(comicData);
            setComicModalVisible(true);
        } catch (err) {
            console.error('Failed to parse comic data:', err);
        }
    };

    const closeComicModal = () => {
        setComicModalVisible(false);
        setCurrentComicData(null);
    };

    const handleLike = async (noteId: number, index: number) => {
        try {
            const result = await ajax({
                method: "POST",
                url: `/api/v1/countryball_card/note/${noteId}/like`,
            });

            const updatedNotes = [...featuredNotes];
            updatedNotes[index] = {
                ...updatedNotes[index],
                likes: result.likes
            };
            setFeaturedNotes(updatedNotes);

            // 更新点赞状态
            setLikedNoteIds(prev => {
                if (prev.includes(noteId)) {
                    return prev.filter(id => id !== noteId);
                } else {
                    return [...prev, noteId];
                }
            });
        } catch (err: any) {
            console.error('Failed to like/unlike note:', err);
        }
    };

    const handleShare = async (noteId: number, index: number) => {
        try {
            // 记录分享
            const result = await ajax({
                method: "POST",
                url: `/api/v1/countryball_card/note/${noteId}/share`,
            });

            // 更新本地状态
            const updatedNotes = [...featuredNotes];
            updatedNotes[index] = {
                ...updatedNotes[index],
                shares: result.shares
            };
            setFeaturedNotes(updatedNotes);

            // 分享链接
            const shareUrl = `${window.location.origin}/note/${noteId}`;

            // 如果有原生分享API，使用它
            if (navigator.share) {
                await navigator.share({
                    title: '国家球日记',
                    text: '看看这篇有趣的国家球日记！',
                    url: shareUrl,
                });
            } else {
                // 否则复制链接到剪贴板
                navigator.clipboard.writeText(shareUrl).then(() => {
                    setShareLinkCopied(true);
                    setTimeout(() => setShareLinkCopied(false), 2000);
                });
            }
        } catch (err) {
            console.error('Failed to share note:', err);
        }
    };

    const handleFeature = async (noteId: number, index: number) => {
        try {
            // 切换精选状态
            const result = await ajax({
                method: "POST",
                url: `/api/v1/countryball_card/note/${noteId}/feature`,
            });

            // 更新本地状态
            const updatedNotes = [...featuredNotes];
            updatedNotes[index] = {
                ...updatedNotes[index],
                featured: result.featured
            };
            setFeaturedNotes(updatedNotes);
        } catch (err) {
            console.error('Failed to toggle featured status:', err);
        }
    };

    return (
        <div className='page-wrap card-page featured-page'>
            <div className="featured-page-content">
                <h3 className="featured-title">管理日记</h3>

                {loading && page === 1 ? (
                    <div className="loading-indicator">
                        <p className="shufa-text">加载中...</p>
                    </div>
                ) : featuredNotes.length === 0 ? (
                    <div className="empty-state">
                        <p className="shufa-text">暂无精选日记</p>
                    </div>
                ) : (
                    <div className="featured-notes-list">
                        {featuredNotes.map((note, index) => (
                            <div
                                key={note.id}
                                className="featured-note-item"
                                ref={index === featuredNotes.length - 1 ? lastNoteRef : null}
                            >
                                <div className="note-header">
                                    <div className="user-info-area">
                                        {note.user.avatarCountry &&
                                            <div className="user-avatar ball-wrap">
                                                <img
                                                    className='ball-pic'
                                                    src={`https://oss.usecubes.cn/countryballs/new_balls/${replaceSpacesWithUnderscoresUsingReplaceAll(note.user.avatarCountry.name_en)}.png`}
                                                    alt={note.country}
                                                />
                                                <img
                                                    className='face-pic'
                                                    src={`https://oss.usecubes.cn/countryballs/faces/${note.user.avatarExpressionType + 1 + note.user.avatarExpressionOrientation}.png`}
                                                />
                                            </div>
                                        }
                                        <div className="note-author shufa-text">{note.user?.username || '匿名用户'}</div>
                                    </div>

                                    {/* 漫画预览按钮移到右上角 */}
                                    {note.comic && note.comic !== '' && (
                                        <div className="comic-button-container">
                                            <div
                                                className="comic-preview shufa-text"
                                                onClick={() => showComic(note.comic)}
                                            >
                                                查看漫画
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <div className="note-content">
                                    <div className="note-avatar ball-wrap">
                                        <img
                                            className='ball-pic'
                                            src={`https://oss.usecubes.cn/countryballs/new_balls/${replaceSpacesWithUnderscoresUsingReplaceAll(note.country)}.png`}
                                            alt={note.country}
                                        />
                                        <img
                                            className='face-pic'
                                            src={`https://oss.usecubes.cn/countryballs/faces/${note.expressionType + 1 + note.expressionOrientation}.png`}
                                        />
                                    </div>
                                    <p className="shufa-text">{note.note}</p>
                                </div>

                                <div className="note-footer">
                                    <div className="note-date shufa-text">{note.date || formatDate(note.created)}</div>

                                    {/* 点赞和分享按钮 */}
                                    <div className="note-actions">
                                        <div
                                            className={`action-button like-button shufa-text ${likedNoteIds.includes(note.id) ? 'liked' : ''}`}
                                            onClick={() => handleLike(note.id, index)}
                                        >
                                            <svg className="action-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" fill="currentColor" />
                                            </svg>
                                            <span>{note.likes || 0}</span>
                                        </div>

                                        {note.isPublic === 1 && (
                                            <div
                                                className="action-button share-button shufa-text"
                                                onClick={() => handleShare(note.id, index)}
                                            >
                                                <svg className="action-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92 1.61 0 2.92-1.31 2.92-2.92s-1.31-2.92-2.92-2.92z" fill="currentColor" />
                                                </svg>
                                                <span>{note.shares || 0}</span>
                                            </div>
                                        )}
                                        
                                        {/* 加精按钮 */}
                                        <div
                                            className={`action-button feature-button shufa-text ${note.featured === 1 ? 'featured' : ''}`}
                                            onClick={() => handleFeature(note.id, index)}
                                        >
                                            <svg className="action-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" fill="currentColor" />
                                            </svg>
                                            <span>{note.featured === 1 ? '取消加精' : '加精'}</span>
                                        </div>

                                        {/* 评论数按钮 */}
                                        <div
                                            className="action-button comment-button shufa-text"
                                            onClick={() => window.location.href = `/note/${note.id}`}
                                        >
                                            <svg className="action-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M20 2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h4l4 4 4-4h4c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z" fill="currentColor" />
                                            </svg>
                                            <span>{note.comments || 0}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}

                        {loadingMore && (
                            <div className="loading-more">
                                <p className="shufa-text">加载更多...</p>
                            </div>
                        )}

                        {!hasMore && featuredNotes.length > 0 && (
                            <div className="end-of-list">
                                <p className="shufa-text">没有更多日记了</p>
                            </div>
                        )}
                    </div>
                )}

                {/* 漫画模态框 */}
                {comicModalVisible && currentComicData && (
                    <div className="comic-modal-overlay" onClick={closeComicModal}>
                        <div className="comic-modal-content" onClick={(e) => e.stopPropagation()}>
                            <div className="comic-modal-header">
                                <h4 className="shufa-text">漫画</h4>
                                <button className="close-button shufa-text" onClick={closeComicModal}>关闭</button>
                            </div>
                            <div className="comic-display">
                                <CommitEditor
                                    mode="view"
                                    comicData={currentComicData}
                                />
                            </div>
                        </div>
                    </div>
                )}

                {/* 分享链接复制提示 */}
                {shareLinkCopied && (
                    <div className="share-notification">
                        <p className="shufa-text">链接已复制到剪贴板</p>
                    </div>
                )}
            </div>
        </div>
    );
}